import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import api from '../../api';
import {
  Scissors, Sparkles, Star, Heart, Droplets, Leaf, Zap, Sun, Moon, Clock,
  Gift, Crown, Smile, Package, Tag, Paintbrush, Wind, Fingerprint, Hand, Eye,
  Check, Camera, X,
} from 'lucide-react';

// ──────────── Palette (extracted from brand palette image) ────────────
const PRODUCT_COLORS = [
  // Fila superior — tonos vibrantes
  { hex: '#70F0D0', name: 'Menta' },
  { hex: '#32D0A4', name: 'Esmeralda' },
  { hex: '#16C8DD', name: 'Cian' },
  { hex: '#08A0B0', name: 'Teal' },
  { hex: '#FDE772', name: 'Amarillo' },
  { hex: '#FF9C72', name: 'Naranja' },
  { hex: '#F75E7A', name: 'Coral' },
  { hex: '#C155AE', name: 'Magenta' },
  { hex: '#5D3CAA', name: 'Violeta' },
  // Tonos medios/apagados
  { hex: '#3CAEA3', name: 'Teal Suave' },
  { hex: '#285F6B', name: 'Teal Oscuro' },
  { hex: '#1E2B33', name: 'Azul Noche' },
  { hex: '#2A4A56', name: 'Azul Profundo' },
  { hex: '#0D1820', name: 'Negro Pizarra' },
  // Grises (5 variedades)
  { hex: '#D4DCE4', name: 'Gris Muy Claro' },
  { hex: '#C2CCD1', name: 'Gris Claro' },
  { hex: '#99A6AA', name: 'Gris Medio' },
  { hex: '#7E8D93', name: 'Gris Pizarra' },
  { hex: '#414E59', name: 'Gris Oscuro' },
  { hex: '#1A252F', name: 'Gris Muy Oscuro' },
];

// ──────────── Icons ────────────
const PRODUCT_ICONS = [
  { key: 'scissors', label: 'Tijeras', C: Scissors },
  { key: 'sparkles', label: 'Brillos', C: Sparkles },
  { key: 'star', label: 'Estrella', C: Star },
  { key: 'heart', label: 'Corazón', C: Heart },
  { key: 'droplets', label: 'Gotas', C: Droplets },
  { key: 'leaf', label: 'Hoja/Spa', C: Leaf },
  { key: 'zap', label: 'Flash', C: Zap },
  { key: 'sun', label: 'Sol/Bronz.', C: Sun },
  { key: 'moon', label: 'Noche', C: Moon },
  { key: 'clock', label: 'Tiempo', C: Clock },
  { key: 'gift', label: 'Regalo', C: Gift },
  { key: 'crown', label: 'Corona', C: Crown },
  { key: 'smile', label: 'Sonrisa', C: Smile },
  { key: 'package', label: 'Caja', C: Package },
  { key: 'tag', label: 'Etiqueta', C: Tag },
  { key: 'paintbrush', label: 'Pincel', C: Paintbrush },
  { key: 'wind', label: 'Secado', C: Wind },
  { key: 'fingerprint', label: 'Uñas', C: Fingerprint },
  { key: 'hand', label: 'Manos', C: Hand },
  { key: 'eye', label: 'Pestañas', C: Eye },
];

// Returns contrasting text color based on background
function getTextColor(hex) {
  if (!hex) return undefined;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? '#1a1a1a' : '#ffffff';
}

export function ProductIconComponent({ iconKey, size = 24, color }) {
  const found = PRODUCT_ICONS.find(i => i.key === iconKey);
  if (!found) return null;
  const { C } = found;
  return <C size={size} strokeWidth={1.5} color={color} />;
}

export { PRODUCT_COLORS, PRODUCT_ICONS, getTextColor };

export default function Products() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', price: '', cost: '', stock_id: '',
    is_custom: false, color: '', icon: '', button_image: null,
  });
  const imgInputRef = useRef(null);

  useEffect(() => { load(); loadStock(); }, []);

  const load = async () => {
    try { const r = await api.get(`/products/${user?.id}`); setProducts(r.data); }
    catch (e) { console.error(e); }
  };

  const loadStock = async () => {
    try { const r = await api.get(`/stock/${user?.id}`); setStockItems(r.data); }
    catch (e) { console.error(e); }
  };

  const handleButtonImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(f => ({ ...f, button_image: ev.target.result }));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const openModal = (p) => {
    if (p) {
      setEditing(p);
      setForm({
        name: p.name,
        description: p.description || '',
        price: p.price.toString(),
        cost: p.cost.toString(),
        stock_id: p.stock_id || '',
        is_custom: p.is_custom || false,
        color: p.color || '',
        icon: p.icon || '',
        button_image: p.button_image || null,
      });
    } else {
      setEditing(null);
      setForm({ name: '', description: '', price: '', cost: '', stock_id: '', is_custom: false, color: '', icon: '', button_image: null });
    }
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || (!form.is_custom && !form.price)) { alert('Nombre y precio son obligatorios'); return; }
    setLoading(true);
    try {
      const data = {
        business_user_id: user?.id,
        name: form.name,
        description: form.description || null,
        price: form.is_custom ? 0 : parseFloat(form.price),
        cost: form.cost ? parseFloat(form.cost) : 0,
        stock_id: form.stock_id || null,
        is_custom: form.is_custom,
        color: form.color || null,
        icon: form.icon || null,
        button_image: form.button_image || null,
      };
      if (editing) await api.put(`/products/${editing.id}`, data);
      else await api.post('/products', data);
      setShowModal(false); load();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setLoading(false); }
  };

  const del = (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    api.delete(`/products/${p.id}`).then(load).catch(() => alert('Error'));
  };

  const getStockName = (stockId) => stockItems.find(s => s.id === stockId)?.description || null;

  const previewTextColor = form.color ? getTextColor(form.color) : 'var(--text)';

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" data-testid="back-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Productos/Servicios</h1>
        <button className="add-btn" data-testid="add-product-btn" onClick={() => openModal()}>+</button>
      </div>
      <div className="content-scroll">
        {products.map(p => {
          const textColor = p.color ? getTextColor(p.color) : undefined;
          const iconEntry = p.icon ? PRODUCT_ICONS.find(i => i.key === p.icon) : null;
          return (
            <div key={p.id} className="card-row" data-testid={`product-row-${p.id}`}>
              {/* Color strip */}
              {p.color && (
                <div style={{ width: 6, alignSelf: 'stretch', borderRadius: '6px 0 0 6px', background: p.color, marginRight: 10, flexShrink: 0 }} />
              )}
              <div className="card-row-info">
                <div className="card-row-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {iconEntry && (
                    <span style={{ color: p.color || 'var(--accent)' }}>
                      <iconEntry.C size={18} strokeWidth={1.5} />
                    </span>
                  )}
                  {!iconEntry && p.stock_id && <span>📦</span>}
                  {p.name}
                </div>
                {p.description && <div className="card-row-detail">{p.description}</div>}
                {p.stock_id && (
                  <div className="card-row-detail" style={{ color: 'var(--accent)' }}>
                    Vinculado a: {getStockName(p.stock_id)}
                  </div>
                )}
                <div className="pricing-row">
                  <span className="accent-text">Precio: ${p.price}</span>
                  <span className="orange-text">{user?.business_type === 'Vendedor' ? 'Adicional' : 'Costo'}: ${p.cost}</span>
                  <span className="green-text">Ganancia: ${p.profit}</span>
                </div>
              </div>
              <div className="card-row-actions">
                <button className="icon-btn-sm" onClick={() => openModal(p)}>✏️</button>
                <button className="icon-btn-sm danger" onClick={() => del(p)}>🗑️</button>
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <div className="empty-state">
            <span className="empty-icon">📦</span>
            <p className="empty-text">No hay productos/servicios</p>
            <p className="empty-subtext">Presiona + para agregar</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflowY: 'auto' }}>
            <h3 className="modal-title">{editing ? 'Editar Producto' : 'Nuevo Producto/Servicio'}</h3>
            <form onSubmit={save}>
              {/* Producto Personalizado Toggle */}
              <label className="checkbox-row" style={{ marginBottom: '16px', background: form.is_custom ? 'rgba(24,203,150,0.1)' : 'var(--surface)', padding: '12px', borderRadius: '8px' }}
                onClick={() => setForm({ ...form, is_custom: !form.is_custom })}>
                <div className={`checkbox-box ${form.is_custom ? 'checked' : ''}`}>{form.is_custom && '✓'}</div>
                <div>
                  <span style={{ fontWeight: '600' }}>Producto Personalizado</span>
                  <p style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '4px' }}>Permite definir precio, descripción y fotos al momento de la venta</p>
                </div>
              </label>

              <div className="form-group">
                <label className="label">Nombre *</label>
                <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="product-name-input" />
              </div>
              <div className="form-group">
                <label className="label">Descripción</label>
                <textarea className="input" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" />
              </div>

              {!form.is_custom && (
                <>
                  <div className="form-group">
                    <label className="label">Precio *</label>
                    <input className="input" type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required data-testid="product-price-input" />
                  </div>
                  <div className="form-group">
                    <label className="label">{user?.business_type === 'Vendedor' ? 'Costo Adicional' : 'Costo'}</label>
                    {user?.business_type === 'Vendedor' && (
                      <p style={{ fontSize: '11px', color: 'var(--text2)', marginBottom: 6 }}>Se suma al costo del stock para calcular la ganancia real</p>
                    )}
                    <input className="input" type="number" step="0.01" value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })} data-testid="product-cost-input" />
                  </div>
                </>
              )}

              {form.is_custom && (
                <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '16px' }}>
                  <p style={{ fontSize: '13px', color: 'var(--text2)' }}>
                    Al seleccionar este producto, podrás definir el precio, descripción, fotos y productos del stock en cada venta.
                  </p>
                </div>
              )}

              {/* ── Color Picker ── */}
              <div className="form-group">
                <label className="label">Color del botón</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {/* No color option */}
                  <button
                    type="button"
                    data-testid="color-none"
                    onClick={() => setForm({ ...form, color: '' })}
                    title="Sin color"
                    style={{
                      width: 32, height: 32, borderRadius: '50%',
                      border: !form.color ? '3px solid var(--accent)' : '2px solid var(--border)',
                      background: 'var(--surface)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', color: 'var(--text2)',
                    }}
                  >✕</button>
                  {PRODUCT_COLORS.map(c => (
                    <button
                      key={c.hex}
                      type="button"
                      data-testid={`color-btn-${c.hex.slice(1)}`}
                      onClick={() => setForm({ ...form, color: c.hex })}
                      title={c.name}
                      style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: c.hex,
                        border: form.color === c.hex ? '3px solid var(--text)' : '2px solid transparent',
                        cursor: 'pointer',
                        boxShadow: form.color === c.hex ? `0 0 0 2px var(--bg), 0 0 0 4px ${c.hex}` : 'none',
                        transition: 'box-shadow 0.15s',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {form.color === c.hex && (
                        <Check size={16} color={getTextColor(c.hex)} strokeWidth={3} />
                      )}
                    </button>
                  ))}
                </div>
                {form.color && (
                  <div style={{ fontSize: '12px', color: 'var(--text2)' }}>
                    Seleccionado: <span style={{ color: form.color, fontWeight: 700 }}>
                      {PRODUCT_COLORS.find(c => c.hex === form.color)?.name || form.color}
                    </span>
                  </div>
                )}
              </div>

              {/* ── Icon Picker ── */}
              <div className="form-group">
                <label className="label">Ícono del botón</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {/* No icon option */}
                  <button
                    type="button"
                    data-testid="icon-none"
                    onClick={() => setForm({ ...form, icon: '' })}
                    title="Sin ícono"
                    style={{
                      width: 44, height: 44, borderRadius: 10,
                      border: !form.icon ? '2px solid var(--accent)' : '2px solid var(--border)',
                      background: 'var(--surface)', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '13px', color: 'var(--text2)',
                    }}
                  >✕</button>
                  {PRODUCT_ICONS.map(ic => (
                    <button
                      key={ic.key}
                      type="button"
                      data-testid={`icon-btn-${ic.key}`}
                      onClick={() => setForm({ ...form, icon: ic.key })}
                      title={ic.label}
                      style={{
                        width: 44, height: 44, borderRadius: 10,
                        border: form.icon === ic.key ? `2px solid ${form.color || 'var(--accent)'}` : '2px solid var(--border)',
                        background: form.icon === ic.key ? (form.color ? `${form.color}22` : 'rgba(93,230,200,0.12)') : 'var(--surface)',
                        cursor: 'pointer',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
                        transition: 'all 0.15s',
                      }}
                    >
                      <ic.C size={18} strokeWidth={1.5} color={form.icon === ic.key ? (form.color || 'var(--accent)') : 'var(--text2)'} />
                      <span style={{ fontSize: '8px', color: 'var(--text2)', lineHeight: 1, textAlign: 'center' }}>{ic.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Button Photo ── */}
              <div className="form-group">
                <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Camera size={14} /> Foto del botón (opcional)
                </label>

                {/* Recommendation tip */}
                <div style={{ background: 'rgba(93,230,200,0.07)', border: '1px solid rgba(93,230,200,0.2)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: '12px', color: 'var(--text2)', lineHeight: 1.5 }}>
                  💡 <strong style={{ color: 'var(--accent)' }}>Recomendación:</strong> Usá una foto con fondo blanco o de color liso para que el botón se vea mejor. Evitá fondos con mucho detalle.
                </div>

                <input
                  type="file"
                  ref={imgInputRef}
                  accept="image/*"
                  onChange={handleButtonImage}
                  style={{ display: 'none' }}
                  data-testid="button-image-input"
                />

                {!form.button_image ? (
                  <button
                    type="button"
                    className="btn-secondary"
                    data-testid="upload-button-image-btn"
                    onClick={() => imgInputRef.current?.click()}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                  >
                    <Camera size={16} /> Cargar foto del producto
                  </button>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img
                      src={form.button_image}
                      alt="Foto del botón"
                      style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border)', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 6 }}>Foto cargada ✓</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ fontSize: '12px', padding: '6px 10px', flex: 1 }}
                          onClick={() => imgInputRef.current?.click()}
                        >
                          Cambiar
                        </button>
                        <button
                          type="button"
                          data-testid="remove-button-image-btn"
                          style={{ fontSize: '12px', padding: '6px 10px', borderRadius: 8, background: 'none', border: '1px solid var(--danger)', color: 'var(--danger)', cursor: 'pointer', flexShrink: 0 }}
                          onClick={() => setForm(f => ({ ...f, button_image: null }))}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Button preview */}
              {(form.name || form.color || form.icon || form.button_image) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 8 }}>Vista previa del botón:</div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {form.button_image ? (
                      // Image preview
                      <div style={{ width: 100, borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                        <img src={form.button_image} alt="preview" style={{ width: '100%', height: 72, objectFit: 'cover', display: 'block' }} />
                        <div style={{ padding: '6px 8px', background: 'var(--surface)' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text)', textAlign: 'center', lineHeight: 1.2 }}>{form.name || 'Nombre'}</div>
                        </div>
                      </div>
                    ) : (
                      // Color/icon preview
                      (() => {
                        let previewStyle = {
                          width: 100, minHeight: 90, borderRadius: 14,
                          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                          gap: 6, padding: '10px 8px',
                        };
                        if (form.color) {
                          const r = parseInt(form.color.slice(1,3), 16);
                          const g = parseInt(form.color.slice(3,5), 16);
                          const b = parseInt(form.color.slice(5,7), 16);
                          const lighter = `rgb(${Math.min(255,r+45)},${Math.min(255,g+45)},${Math.min(255,b+45)})`;
                          const darker  = `rgb(${Math.max(0,r-30)},${Math.max(0,g-30)},${Math.max(0,b-30)})`;
                          previewStyle = {
                            ...previewStyle,
                            background: `linear-gradient(145deg, ${lighter} 0%, ${form.color} 50%, ${darker} 100%)`,
                            boxShadow: ['inset 0 1px 1px rgba(255,255,255,0.30)', 'inset 0 -2px 3px rgba(0,0,0,0.18)', `0 6px 16px ${form.color}66`, '0 2px 5px rgba(0,0,0,0.35)'].join(', '),
                            border: 'none',
                          };
                        } else {
                          previewStyle = { ...previewStyle, background: 'var(--surface)', border: '1px solid var(--border)' };
                        }
                        return (
                          <div style={previewStyle}>
                            {form.icon && (() => { const ic = PRODUCT_ICONS.find(i => i.key === form.icon); return ic ? <ic.C size={28} strokeWidth={1.5} color={form.color ? previewTextColor : 'var(--accent)'} /> : null; })()}
                            {!form.icon && !form.color && <Tag size={24} strokeWidth={1.5} color="var(--text2)" />}
                            <span style={{ fontSize: '12px', fontWeight: 600, textAlign: 'center', lineHeight: 1.2, color: form.color ? previewTextColor : 'var(--text)' }}>{form.name || 'Nombre'}</span>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              {/* Stock Link */}
              <div className="form-group">
                <label className="label">📦 Vincular con Stock (opcional)</label>
                <select className="input" value={form.stock_id} onChange={e => setForm({ ...form, stock_id: e.target.value })} data-testid="product-stock-select">
                  <option value="">Sin vincular</option>
                  {stockItems.map(s => (
                    <option key={s.id} value={s.id}>{s.description} ({s.quantity} disponibles)</option>
                  ))}
                </select>
                {form.stock_id && (
                  <p className="card-row-detail" style={{ marginTop: '8px', fontSize: '12px' }}>
                    Al registrar una venta, se descontará automáticamente del stock
                  </p>
                )}
              </div>

              {stockItems.length === 0 && (
                <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: '8px', marginBottom: '16px', textAlign: 'center' }}>
                  <p className="card-row-detail">¿Quieres vincular productos con stock?</p>
                  <button type="button" className="new-client-btn" style={{ marginTop: '8px' }}
                    onClick={() => { setShowModal(false); navigate('/business/stock'); }}>
                    📦 Ir a Stock
                  </button>
                </div>
              )}

              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="save-product-btn">
                  {loading ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
